import { format, isSameDay } from 'date-fns';

import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import { appDateToDate } from './appDate';

export type MessageListEntry =
  | { kind: 'message'; message: ExtendedCommunityChannelMessage }
  | { kind: 'date-separator'; key: string; label: string };

/**
 * Walk messages (oldest-first) and intersperse a `date-separator`
 * entry between groups that fall on different calendar days. Matches
 * the "September 2nd, 2025" / "March 9th" pills in the design.
 *
 * Input order matters — pass messages in chronological (oldest-first)
 * order. The list scrolls bottom-most-recent, so callers typically
 * sort ascending before piping through here.
 */
export function groupMessagesByDate(
  messages: ExtendedCommunityChannelMessage[],
): MessageListEntry[] {
  if (messages.length === 0) return [];

  const entries: MessageListEntry[] = [];
  let prevDate: Date | undefined;

  for (const message of messages) {
    const date = appDateToDate(message.createdAt);
    if (!prevDate || !isSameDay(prevDate, date)) {
      entries.push({
        kind: 'date-separator',
        key: format(date, 'yyyy-MM-dd'),
        label: formatDateSeparator(date),
      });
      prevDate = date;
    }
    entries.push({ kind: 'message', message });
  }

  return entries;
}

function formatDateSeparator(date: Date): string {
  // "March 9th" / "September 2nd, 2025" — match the design's mixed
  // form: drop the year only when it's the current year.
  const now = new Date();
  if (date.getFullYear() === now.getFullYear()) {
    return format(date, 'MMMM do');
  }
  return format(date, 'MMMM do, yyyy');
}
